import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts'
import type { SimulationResult } from '../engine/types'

interface Props {
  result: SimulationResult
  retirementTarget: number
  realityData?: Array<{ year: number; netWorth: number }>
  inflationMean: number
  startYear: number
}

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`
  return `$${Math.round(v)}`
}

/** HC-CONSTANT-CURRENCY: deflate nominal value to startYear real dollars. yearIndex is 0-based. */
function real(nominal: number, yearIndex: number, inflationMean: number): number {
  return Math.round(nominal / Math.pow(1 + inflationMean, yearIndex))
}

export function NetWorthChart({ result, retirementTarget, realityData, inflationMean, startYear }: Props) {
  const data = result.netWorthBands.map((band, i) => {
    const reality = realityData?.find(r => r.year === band.year)
    return {
      year: band.year,
      p10: real(band.p10, i, inflationMean),
      p25: real(band.p25, i, inflationMean),
      p50: real(band.p50, i, inflationMean),
      p75: real(band.p75, i, inflationMean),
      p90: real(band.p90, i, inflationMean),
      actual: reality ? real(reality.netWorth, i, inflationMean) : undefined,
    }
  })

  return (
    <div className="chart-card">
      <h3>Net Worth Projection</h3>
      <p className="chart-subtitle">
        Monte Carlo percentile bands ({result.iterations.toLocaleString()} iterations) ·{' '}
        Target: {fmt(real(retirementTarget, 0, inflationMean))} ·{' '}
        <strong>All values in {startYear} real dollars</strong>
      </p>
      <ResponsiveContainer width="100%" height={340}>
        <AreaChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
          <defs>
            <linearGradient id="nwGrad90" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.08} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01} />
            </linearGradient>
            <linearGradient id="nwGrad75" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={v => fmt(v)}
            tick={{ fontSize: 12 }}
            width={70}
          />
          <Tooltip
            formatter={(v: number) => fmt(v)}
            labelFormatter={l => `Year: ${l}`}
          />
          <Legend />
          <ReferenceLine
            y={retirementTarget}
            stroke="#10b981"
            strokeDasharray="6 3"
            label={{ value: 'Target', fill: '#10b981', fontSize: 11 }}
          />
          <Area
            type="monotone"
            dataKey="p90"
            stroke="#93c5fd"
            fill="url(#nwGrad90)"
            strokeWidth={1}
            name="p90"
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="p75"
            stroke="#60a5fa"
            fill="url(#nwGrad75)"
            strokeWidth={1}
            name="p75"
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="p50"
            stroke="#2563eb"
            fill="none"
            strokeWidth={2.5}
            name="Median"
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="p25"
            stroke="#60a5fa"
            fill="none"
            strokeWidth={1}
            name="p25"
            dot={false}
            strokeDasharray="4 2"
          />
          <Area
            type="monotone"
            dataKey="p10"
            stroke="#93c5fd"
            fill="none"
            strokeWidth={1}
            name="p10"
            dot={false}
            strokeDasharray="2 3"
          />
          {realityData && realityData.length > 0 && (
            <Area
              type="monotone"
              dataKey="actual"
              stroke="#f59e0b"
              fill="none"
              strokeWidth={2}
              name="Actual"
              dot={{ r: 5, fill: '#f59e0b' }}
              connectNulls={false}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
